import { useCallback, useEffect, useMemo, useState } from "react";
import { MdInsertChartOutlined, MdRefresh } from "react-icons/md";
import attendanceService from "services/attendanceService";
import Button from "components/button";
import Card, { CardHeader } from "components/card";
import Chart from "components/charts/Chart";
import { DatePicker, Select } from "components/fields";
import { PageContainer, PageHeader } from "components/page";
import { EmptyState, ErrorState } from "components/states";
import { getApiErrorMessage } from "api";
import { colorway } from "lib/chartTheme";
import { formatCurrency, formatDate, formatNumber } from "utils/format";

const SOURCE_OPTIONS = [
  { value: "all", label: "All Sources" },
  { value: "atrium", label: "Atrium Pulse" },
  { value: "churchmetrics", label: "Church Metrics" },
];

const CURRENCY_KEYWORDS = ["contribution", "fund", "giving", "offering", "tithe"];

const METRIC_LABELS = {
  attendance: "Attendance",
  kids: "Kids",
  salvations: "Salvations",
  volunteers: "Volunteers",
  contributions: "Contributions",
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);

  return {
    startDate: startDate.toISOString().split("T")[0],
    endDate: endDate.toISOString().split("T")[0],
  };
};

// Converts snake_case or camelCase metric keys to Title Case.
const formatMetricName = (key) =>
  METRIC_LABELS[key] ||
  key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .trim();

const isCurrencyMetric = (key) => {
  const lowerKey = key.toLowerCase();
  return CURRENCY_KEYWORDS.some((keyword) => lowerKey.includes(keyword));
};

const formatWholeCurrency = (value) => formatCurrency(value, { maximumFractionDigits: 0 });
const formatWholeNumber = (value) => formatNumber(Math.round(value));

// Download-only toolbar so each chart can still be exported as CSV, SVG or PNG.
const exportToolbar = (filename) => ({
  show: true,
  tools: {
    download: true,
    selection: false,
    zoom: false,
    zoomin: false,
    zoomout: false,
    pan: false,
    reset: false,
  },
  export: {
    csv: { filename },
    svg: { filename },
    png: { filename },
  },
});

const Charts = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [filters, setFilters] = useState(() => ({
    ...getCurrentMonthRange(),
    source: "all",
  }));

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await attendanceService.getMetricsSummary({
        startDate: filters.startDate,
        endDate: filters.endDate,
      });

      let services = response.services || [];
      if (filters.source === "atrium") {
        services = services.filter((row) => row.source !== "imported");
      } else if (filters.source === "churchmetrics") {
        services = services.filter((row) => row.source === "imported");
      }

      setReportData(services);

      const metricsSet = new Set();
      services.forEach((service) => {
        if (service.metrics) {
          Object.keys(service.metrics).forEach((key) => metricsSet.add(key));
        }
      });
      setAvailableMetrics(Array.from(metricsSet).sort());
    } catch (err) {
      console.error("Error loading report:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate, filters.source]);

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      loadReport();
    }
  }, [filters.startDate, filters.endDate, loadReport]);

  // Sums every metric per day, in date order.
  const chartData = useMemo(() => {
    if (reportData.length === 0 || availableMetrics.length === 0) return null;

    const sortedData = [...reportData].sort((a, b) => new Date(a.date) - new Date(b.date));

    const groupedByDate = sortedData.reduce((acc, service) => {
      const date = formatDate(service.date);
      if (!acc[date]) {
        acc[date] = {};
        availableMetrics.forEach((metric) => {
          acc[date][metric] = 0;
        });
      }
      availableMetrics.forEach((metric) => {
        acc[date][metric] += service.metrics?.[metric] || 0;
      });
      return acc;
    }, {});

    const dates = Object.keys(groupedByDate);
    const metrics = {};
    availableMetrics.forEach((metric) => {
      metrics[metric] = dates.map((date) => groupedByDate[date][metric]);
    });

    return { dates, metrics };
  }, [reportData, availableMetrics]);

  const dateRange = `${filters.startDate}_to_${filters.endDate}`;
  const categories = chartData?.dates;

  const overview = useMemo(
    () => ({
      series: availableMetrics.map((metric) => ({
        name: formatMetricName(metric),
        data: chartData?.metrics[metric] || [],
      })),
      options: {
        chart: { toolbar: exportToolbar(`All_Metrics_Overview_${dateRange}`) },
        stroke: { curve: "smooth" },
        xaxis: { categories: categories || [] },
        yaxis: { labels: { formatter: formatWholeNumber } },
      },
    }),
    [availableMetrics, chartData, categories, dateRange]
  );

  const metricCharts = useMemo(
    () =>
      availableMetrics.map((metric, index) => {
        const name = formatMetricName(metric);
        const isCurrency = isCurrencyMetric(metric);
        return {
          metric,
          name,
          series: [{ name, data: chartData?.metrics[metric] || [] }],
          options: {
            chart: { toolbar: exportToolbar(`${name}_${dateRange}`) },
            colors: [colorway[index % colorway.length]],
            stroke: { curve: "smooth", width: 3 },
            xaxis: { categories: categories || [] },
            ...(isCurrency && { yaxis: { labels: { formatter: formatWholeCurrency } } }),
            tooltip: {
              y: { formatter: (value) => (isCurrency ? formatCurrency(value) : formatNumber(value)) },
            },
          },
        };
      }),
    [availableMetrics, chartData, categories, dateRange]
  );

  const setFilter = (key) => (e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  const setDate = (key) => (value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const renderCharts = () => {
    if (error) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(error, "Could not load attendance charts")}
            onRetry={loadReport}
          />
        </Card>
      );
    }

    if (loading) {
      return (
        <>
          <Card padding="md" className="gap-4">
            <CardHeader title="All Metrics Overview" />
            <Chart loading height="lg" series={[]} />
          </Card>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {[0, 1].map((i) => (
              <Card key={i} padding="md">
                <Chart loading height={300} series={[]} />
              </Card>
            ))}
          </div>
        </>
      );
    }

    if (!chartData || chartData.dates.length === 0) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdInsertChartOutlined}
            title="No Data for This Date Range"
            description="Try a different date range or source."
          />
        </Card>
      );
    }

    return (
      <>
        <Card padding="md" className="gap-4">
          <CardHeader title="All Metrics Overview" />
          <Chart type="line" height="lg" series={overview.series} options={overview.options} />
        </Card>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {metricCharts.map(({ metric, name, series, options }) => (
            <Card key={metric} padding="md" className="gap-4">
              <CardHeader title={name} />
              <Chart type="line" height={300} series={series} options={options} />
            </Card>
          ))}
        </div>
      </>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Charts"
        description="Visualize attendance metrics and trends"
        actions={
          <Button variant="secondary" leftIcon={<MdRefresh />} onClick={loadReport} loading={loading}>
            Refresh
          </Button>
        }
      />

      <Card padding="md">
        <div className="flex flex-wrap items-end gap-4">
          <DatePicker
            label="Start Date"
            valueFormat="YYYY-MM-DD"
            value={filters.startDate}
            onChange={setDate("startDate")}
            className="w-full sm:w-52"
          />
          <DatePicker
            label="End Date"
            valueFormat="YYYY-MM-DD"
            value={filters.endDate}
            onChange={setDate("endDate")}
            className="w-full sm:w-52"
          />
          <Select
            label="Source"
            value={filters.source}
            onChange={setFilter("source")}
            options={SOURCE_OPTIONS}
            className="w-full sm:w-52"
          />
        </div>
      </Card>

      {renderCharts()}
    </PageContainer>
  );
};

export default Charts;
